import React, { useEffect, useMemo } from 'react';
import { useAdaptiveImagePreloader } from '../../hooks/useImagePreloader';
import { imagePreloader, ImagePreloaderService } from '../../services/ImagePreloader';
import { performanceMonitor } from '../../utils/PerformanceMonitor';

interface ProductImagePreloaderProps {
  product: any;
  currentColor?: string;
  onPreloadComplete?: (stats: any) => void;
  debug?: boolean;
}

const ProductImagePreloader: React.FC<ProductImagePreloaderProps> = ({
  product,
  currentColor = 'black',
  onPreloadComplete,
  debug = false,
}) => {
  const { preloadColorImages, statistics, networkSpeed, isPreloading } = useAdaptiveImagePreloader();

  // Extract available colors from product images
  const availableColors = useMemo(() => {
    if (!product?.images?.edges) return ['black'];

    const colors = new Set<string>();
    product.images.edges.forEach((edge: any) => {
      if (edge.node?.altText) {
        const altText = edge.node.altText.toLowerCase();
        const colorMatch = altText.match(/ - ([^-]+)$/);
        if (colorMatch && colorMatch[1] !== 'all') {
          colors.add(colorMatch[1]);
        }
      }
    });

    return Array.from(colors);
  }, [product]);

  // Preload images when product data becomes available
  useEffect(() => {
    if (!product?.images?.edges?.length) return;

    const startPreloading = async () => {
      try {
        if (debug) {
          console.log('🖼️ Starting image preloading...', {
            totalImages: product.images.edges.length,
            availableColors,
            currentColor,
          });
        }

        const results = await preloadColorImages(product.images.edges, availableColors);

        if (onPreloadComplete) {
          onPreloadComplete(imagePreloader.getStatistics());
        }

        if (debug) {
          console.log('✅ Image preloading completed:', imagePreloader.getStatistics());
        }
      } catch (error) {
        console.error('❌ Image preloading failed:', error);
      }
    };

    // Delay to avoid blocking critical rendering
    const timer = setTimeout(startPreloading, 100);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, availableColors, preloadColorImages, onPreloadComplete, debug]);

  // Preload images for newly selected color
  useEffect(() => {
    if (!product?.images?.edges?.length || !currentColor) return;

    const preloadCurrentColorImages = async () => {
      const startTime = Date.now();
      
      if (debug) {
        console.log(`🎨 Preloading images for color: ${currentColor}`);
      }

      // Preload all sizes for the selected color
      const urls = imagePreloader.generateImageUrls(
        product.images.edges,
        {
          ...ImagePreloaderService.PRIORITY_CONFIGS.HIGH,
          colors: [currentColor, 'all'],
          sizes: [
            ImagePreloaderService.SIZES.THUMBNAIL,
            ImagePreloaderService.SIZES.DISPLAY,
            ImagePreloaderService.SIZES.GALLERY,
          ],
        },
        currentColor
      );

      await imagePreloader.preloadImages(urls, {
        ...ImagePreloaderService.PRIORITY_CONFIGS.HIGH,
        colors: [currentColor, 'all'],
      });

      // Track color change performance
      const totalTime = Date.now() - startTime;
      performanceMonitor.trackColorChange(currentColor, urls.length, totalTime);
    };

    // Small delay to avoid blocking color change UI
    const timer = setTimeout(preloadCurrentColorImages, 50);
    return () => clearTimeout(timer);
  }, [currentColor, product, debug]);

  // Debug component - shows preloading statistics
  if (debug && process.env.NODE_ENV === 'development') {
    return (
      <div style={{
        position: 'fixed',
        top: '10px',
        right: '10px',
        background: 'rgba(0, 0, 0, 0.8)',
        color: 'white',
        padding: '10px',
        borderRadius: '5px',
        fontSize: '12px',
        fontFamily: 'monospace',
        zIndex: 9999,
        minWidth: '200px',
      }}>
        <div><strong>Image Preloader Debug</strong></div>
        <div>Network: {networkSpeed}</div>
        <div>Loading: {isPreloading ? '⏳' : '✅'}</div>
        <div>Total: {statistics.total}</div>
        <div>Loaded: {statistics.loaded}</div>
        <div>Cache Ratio: {Math.round(statistics.cacheRatio * 100)}%</div>
        <div>Avg Load: {statistics.avgLoadTime}ms</div>
        <div>Active: {statistics.activeLoads}</div>
        <div>Queued: {statistics.queuedLoads}</div>
        <div>Colors: {availableColors.join(', ')}</div>
      </div>
    );
  }

  return null;
};

export default ProductImagePreloader;